import { useEffect, useState, useCallback } from "react";
import {
  Box,
  Card,
  Typography,
  IconButton,
  TextField,
  Button,
  Divider,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Chip,
  Avatar,
  Checkbox,
  FormControlLabel,
  Pagination,
} from "@mui/material";
import { Delete, Edit, Save, Close, Refresh, Add, Visibility } from "@mui/icons-material";
import { styled, useTheme } from "@mui/material/styles";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import { useToast } from "OtherReusableFunctionality/ToastContext";
import { usePageTitle } from "contexts/PageTitleContext";
import { useDeleteConfirmation } from "contexts/DeleteConfirmationContext";

const PageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const HeaderCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: "0.875rem",
  padding: "12px 16px",
  "&.header": {
    fontWeight: 600,
    backgroundColor: theme.palette.grey[100],
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const TableCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const Muted = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

const ImageContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 8,
});

const Users = () => {
  const theme = useTheme();
  const toast = useToast();
  const { setPageTitle } = usePageTitle();
  const { showDeleteConfirmation } = useDeleteConfirmation();
  const [errors, setErrors] = useState({
    mobile_number: '',
    email: ''
  });

  // Loading state
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [userTypes, setUserTypes] = useState([]);
  const [loadingUserTypes, setLoadingUserTypes] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    user_type: "",
    first_name: "",
    last_name: "",
    mobile_number: "",
    email: "",
    pincode: "",
    country: "",
    state: "",
    district: "",
    tehsil: "",
    village: "",
    password: "",
    type_of_user: "",
    status: "Active",
  });
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    page_size: 10,
    total_items: 0,
    total_pages: 1,
    has_next: false,
    has_previous: false,
    next_page_number: null,
    previous_page_number: null,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [10, 25, 50, 100];

  const validateMobile = (value) => {
    if (!value) return 'Mobile number is required';
    if (!/^[6-9]\d{9}$/.test(value)) return 'Enter valid 10-digit mobile number';
    return '';
  };
  
  const validateEmail = (value) => {
    if (!value) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Enter valid email address';
    return '';
  };

  useEffect(() => {
    setPageTitle("Users");
    fetchUsers(1, 10);
    fetchUserTypes();
  }, [setPageTitle]);

  // Fetch users with pagination
  const fetchUsers = async (pageParam = 1, pageSizeParam = 10) => {
    setLoading(true);
    try {
      const url = `${API_ENDPOINTS.Get_Users}${API_ENDPOINTS.Get_Users.includes('?') ? '&' : '?'}page=${pageParam}&page_size=${pageSizeParam}`;
      const res = await apiService.get(url);

      const list = res?.data?.data || res?.data || [];
      const active = Array.isArray(list) ? list.filter(u => !u.is_deleted) : [];
      setUsers(active);

      const paginationData = res?.pagination || res?.data?.pagination || {};
      if (Object.keys(paginationData).length > 0) {
        setPagination({
          current_page: paginationData.current_page ?? pageParam,
          page_size: paginationData.page_size ?? pageSizeParam,
          total_items: paginationData.total_items ?? 0,
          total_pages: paginationData.total_pages ?? 1,
          has_next: paginationData.has_next ?? false,
          has_previous: paginationData.has_previous ?? false,
          next_page_number: paginationData.next_page_number ?? null,
          previous_page_number: paginationData.previous_page_number ?? null,
        });
        setCurrentPage(paginationData.current_page ?? pageParam);
        setPageSize(paginationData.page_size ?? pageSizeParam);
      } else {
        setPagination(prev => ({ ...prev, total_items: active.length, total_pages: 1, current_page: 1, page_size: pageSizeParam }));
      }
    } catch (err) {
      console.error('Error fetching users', err);
      toast.showError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleUsersPageChange = (_, value) => {
    setCurrentPage(value);
    fetchUsers(value, pageSize);
  };

  const handleUsersPageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setPageSize(newSize);
    setCurrentPage(1);
    fetchUsers(1, newSize);
  };

  // Fetch user types for dropdown
  const fetchUserTypes = async () => {
    setLoadingUserTypes(true);
    try {
      const res = await apiService.get(API_ENDPOINTS.Get_User_types);
      const list = res?.data?.data || res?.data || [];
      setUserTypes(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Error fetching user types', err);
    } finally {
      setLoadingUserTypes(false);
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingId(user.id);
      setFormData({
        user_type: user.user_type?.id || "",
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        mobile_number: user.mobile_number || "",
        email: user.email || "",
        pincode: user.pincode || "",
        country: user.country || "",
        state: user.state || "",
        district: user.district || "",
        tehsil: user.tehsil || "",
        village: user.village || "",
        password: "",
        type_of_user: user.type_of_user || "",
        status: user.status || "Active",
      });
    } else {
      setEditingId(null);
      setFormData({
        user_type: "",
        first_name: "",
        last_name: "",
        mobile_number: "",
        email: "",
        pincode: "",
        country: "",
        state: "",
        district: "",
        tehsil: "",
        village: "",
        password: "",
        type_of_user: "",
        status: "Active",
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
  };

  const handleSave = async () => {
    if (!formData.first_name.trim() || !formData.mobile_number.trim() || !formData.email.trim()) {
      toast.showError('First name, mobile and email are required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        user_type: Number(formData.user_type) || null,
        first_name: formData.first_name.trim(),
        last_name: formData.last_name?.trim() || null,
        mobile_number: formData.mobile_number?.trim() || null,
        email: formData.email?.trim() || null,
        pincode: formData.pincode || null,
        country: formData.country || null,
        state: formData.state || null,
        district: formData.district || null,
        tehsil: formData.tehsil || null,
        village: formData.village || null,
        password: formData.password || undefined,
        type_of_user: formData.type_of_user || null,
        status: formData.status || 'Active'
      };

      let res;
      if (editingId) {
        // Update requires user_id
        res = await apiService.put(API_ENDPOINTS.Update_Users, { ...payload, user_id: editingId });
      } else {
        res = await apiService.post(API_ENDPOINTS.Add_Users, payload);
      }

      console.log('Save user response', res);

      if (res?.success) {
        // Using new helper methods for add/update operations
        if (editingId) {
          toast.showUpdateSuccess('User');
        } else {
          toast.showAddSuccess('User');
        }
        handleCloseDialog();
        await fetchUsers(currentPage, pageSize);
      } else {
        toast.showError(res?.message || 'Failed to save user');
      }
    } catch (err) {
      console.error('Error saving user', err);
      const msg = err?.response?.data?.message || err?.message || 'Failed to save user';
      toast.showError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    const confirmed = await showDeleteConfirmation({
      title: 'Delete User',
      message: 'Are you sure you want to delete this user?',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel'
    });

    if (!confirmed) return;

    setDeletingId(id);
    try {
      const res = await apiService.delete(`${API_ENDPOINTS.Delete_Users}?user_id=${id}`);
      console.log('Delete response', res);
      if (res?.success) {
        // Using new helper method for delete operation
        toast.showDeleteSuccess('User');
        setUsers(prev => prev.filter(u => u.id !== id));
        await fetchUsers(currentPage, pageSize);
      } else {
        toast.showDeleteError('User');
      }
    } catch (err) {
      console.error('Error deleting user', err);
      toast.showDeleteError('User');
    } finally {
      setDeletingId(null);
    }
  };

  const handleEdit = (user, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    handleOpenDialog(user);
  };

  return (
    <PageContainer>
      <HeaderCard>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Users
          </Typography>
          <Muted variant="body2">Manage application users</Muted>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>Rows</InputLabel>
            <Select value={pageSize} label="Rows" onChange={handleUsersPageSizeChange}>
              {pageSizeOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button startIcon={<Add />} variant="contained" onClick={() => handleOpenDialog()}>
            Add User
          </Button>
          <Button startIcon={<Refresh />} variant="outlined" onClick={() => fetchUsers(currentPage, pageSize)} disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Box>
      </HeaderCard>

      <TableCard>
        <TableContainer component={Paper} sx={{
        boxShadow: 3,
        borderRadius: 2,
        maxHeight: 500,
        overflow: 'auto',
          /* Firefox */
          scrollbarWidth: "thin",
          scrollbarColor:
            theme.palette.mode === "dark"
              ? "#555 #1e1e1e"
              : "#c1c1c1 #f1f1f1",
          /* Chrome, Edge, Safari */
          "&::-webkit-scrollbar": {
            height: 8,
            width: 8,
          },
          "&::-webkit-scrollbar-track": {
            background:
              theme.palette.mode === "dark"
                ? "#1e1e1e"
                : "#f1f1f1",
            borderRadius: 8,
          },
          "&::-webkit-scrollbar-thumb": {
            background:
              theme.palette.mode === "dark"
                ? "#555"
                : "#c1c1c1",
            borderRadius: 8,
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background:
              theme.palette.mode === "dark"
                ? "#777"
                : "#a8a8a8",
          },
        // padding: "16px",
      }}>
          <Table stickyHeader>
            <TableHead sx={{ position: "sticky", top: 0, zIndex: 20 }}>
              <TableRow>
                <StyledTableCell className="header" align="center" sx={{ width: '60px' }}>Sr No</StyledTableCell>
                <StyledTableCell className="header" align="center">Name</StyledTableCell>
                <StyledTableCell className="header" align="center">Mobile</StyledTableCell>
                <StyledTableCell className="header" align="center">Email</StyledTableCell>
                <StyledTableCell className="header" align="center">User Type</StyledTableCell>
                <StyledTableCell className="header" align="center">Status</StyledTableCell>
            <StyledTableCell
              className="header"
              sx={{
                position: "sticky",
                right: 0,
                top: 0,
                zIndex: 9999,
                width: 150,
                boxShadow: "-4px 0 6px rgba(0,0,0,0.15) !important",
                backgroundColor: (theme) =>
                  theme.palette.mode === "light" ? "rgba(246, 248, 247)" : theme.palette.background.paper,
              }}
            >
              Actions
            </StyledTableCell>              
            </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={28} />
                    <Typography variant="body2" sx={{ mt: 1 }}>Loading users...</Typography>
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">No users found. Click "Add User" to create one.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user, idx) => (
                  <StyledTableRow key={user.id}>
                    <StyledTableCell align="center"><Chip label={`#${idx + 1}`} size="small"  variant="outlined" /></StyledTableCell>
                    <StyledTableCell align="center">
                      <Typography variant="body1" fontWeight={600}>{user.first_name} {user.last_name || ''}</Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center"><Typography variant="body2">{user.mobile_number || 'N/A'}</Typography></StyledTableCell>
                    <StyledTableCell align="center"><Typography variant="body2">{user.email || 'N/A'}</Typography></StyledTableCell>
                    <StyledTableCell align="center"><Typography variant="body2">{user.user_type?.name || 'N/A'}</Typography></StyledTableCell>
                    <StyledTableCell align="center"><Chip label={user.status || 'N/A'} size="small" color={user.status === 'Active' ? 'success' : 'default'} variant="outlined" /></StyledTableCell>
                    <StyledTableCell align="center"  sx={{ position: "sticky",
                      right: 0,
                      zIndex: 4,
                      backgroundColor: theme.palette.background.paper, // adapts to dark/light
                      display: "flex", 
                      gap: 1,
                      minWidth: 150,
                      padding:"50px 20px"}}>
                      <Box display="flex" gap={1} justifyContent="center" alignItems="center">
                        <Tooltip title="Edit"><span><IconButton color="primary" size="small" onClick={(e) => handleEdit(user, e)}><Edit fontSize="small" /></IconButton></span></Tooltip>
                        <Tooltip title="Delete"><span><IconButton color="error" size="small" onClick={(e) => handleDelete(user.id, e)} disabled={deletingId === user.id}>{deletingId === user.id ? <CircularProgress size={18} /> : <Delete fontSize="small" />}</IconButton></span></Tooltip>
                      </Box>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              )}
            </TableBody>
      </Table>
    </TableContainer>
      </TableCard>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {pagination.total_items > 0
            ? `Showing ${(pagination.current_page - 1) * pagination.page_size + 1} - ${Math.min(pagination.current_page * pagination.page_size, pagination.total_items)} of ${pagination.total_items} users`
            : (users.length > 0 ? `Showing ${users.length} users` : 'No users')}
        </Typography>
        {pagination.total_pages > 1 && (
          <Pagination count={pagination.total_pages} page={currentPage} onChange={handleUsersPageChange} color="primary" />
        )}
      </Box>

      {/* User Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{editingId ? 'Edit User' : 'Add User'}</Typography>
            <IconButton onClick={handleCloseDialog} size="small"><Close /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>User Type</InputLabel>
              <Select value={formData.user_type} label="User Type" onChange={(e) => setFormData(prev => ({ ...prev, user_type: e.target.value }))}>
                <MenuItem value=""><em>None</em></MenuItem>
                {userTypes.map(ut => (
                  <MenuItem key={ut.id} value={ut.id}>{ut.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField label="Status" value={formData.status} onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))} fullWidth />

            <TextField label="First Name" value={formData.first_name} onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))} fullWidth required />
            <TextField label="Last Name" value={formData.last_name} onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))} fullWidth />

            <TextField
                    label="Mobile Number"
                    value={formData.mobile_number}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      setFormData(prev => ({ ...prev, mobile_number: value }));
                      setErrors(prev => ({ ...prev, mobile_number: validateMobile(value) }));
                    }}
                    error={!!errors.mobile_number}
                    helperText={errors.mobile_number}
                    fullWidth
                    required
                    inputProps={{ maxLength: 10 }}
                  />

                  <TextField
                    label="Email"
                    value={formData.email}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData(prev => ({ ...prev, email: value }));
                      setErrors(prev => ({ ...prev, email: validateEmail(value) }));
                    }}
                    error={!!errors.email}
                    helperText={errors.email}
                    fullWidth
                    required
                  />

            {/* <TextField label="Password" value={formData.password} onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))} fullWidth type="password" helperText={editingId ? 'Leave blank to keep existing password' : ''} />

            <TextField label="Type of User" value={formData.type_of_user} onChange={(e) => setFormData(prev => ({ ...prev, type_of_user: e.target.value }))} fullWidth />

            <TextField label="Pincode" value={formData.pincode} onChange={(e) => setFormData(prev => ({ ...prev, pincode: e.target.value }))} fullWidth />
            <TextField label="Country" value={formData.country} onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))} fullWidth />

            <TextField label="State" value={formData.state} onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))} fullWidth />
            <TextField label="District" value={formData.district} onChange={(e) => setFormData(prev => ({ ...prev, district: e.target.value }))} fullWidth />

            <TextField label="Tehsil" value={formData.tehsil} onChange={(e) => setFormData(prev => ({ ...prev, tehsil: e.target.value }))} fullWidth />
            <TextField label="Village" value={formData.village} onChange={(e) => setFormData(prev => ({ ...prev, village: e.target.value }))} fullWidth /> */}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !formData.first_name.trim() || !formData.mobile_number.trim() || !formData.email.trim()} startIcon={saving ? <CircularProgress size={16} /> : <Save />}>
            {saving ? 'Saving...' : editingId ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default Users;